(() => {
	'use strict';

	if (customElements.get('ticker-bar')) return;

	const getDebounce = () => {
		const { theme } = window;
		if (theme && typeof theme.debounce === 'function') {
			return theme.debounce;
		}
		return (fn) => fn;
	};

	const debounce = getDebounce();

	class TickerBar extends HTMLElement {
		constructor() {
			super();
			this.autoplay = this.hasAttribute('data-autoplay');
			this.resizeEvent = debounce(() => this.enqueueMeasurement(), 100);
			this.rafId = null;
			this.pauseListenersAttached = false;
		}

		connectedCallback() {
			this.scale = this.querySelector('[data-ticker-scale]');
			this.text = this.querySelector('[data-ticker-text]');

			if (!this.scale || !this.text) return;

			if (!this.comparitor) {
				this.comparitor = this.text.cloneNode(true);
				this.comparitor.classList.add('ticker__comparitor');
				this.comparitor.setAttribute('aria-hidden', 'true');
				this.appendChild(this.comparitor);
			}

			this.scale.classList.remove('ticker--unloaded');
			document.addEventListener('theme:resize:width', this.resizeEvent);
			this.enqueueMeasurement();
		}

		disconnectedCallback() {
			document.removeEventListener('theme:resize:width', this.resizeEvent);
			if (this.rafId) {
				cancelAnimationFrame(this.rafId);
				this.rafId = null;
			}
		}

		enqueueMeasurement() {
			if (this.rafId) return;
			this.rafId = requestAnimationFrame(() => {
				this.rafId = null;
				this.checkWidth();
			});
		}

		getHorizontalPadding() {
			const styles = window.getComputedStyle(this);
			const left = parseFloat(styles.paddingLeft) || 0;
			const right = parseFloat(styles.paddingRight) || 0;
			return left + right;
		}

		checkWidth() {
			if (!this.comparitor) return;

			const availableWidth = Math.max(0, this.clientWidth - this.getHorizontalPadding());
			const textWidth = this.comparitor.getBoundingClientRect().width;

			if (!textWidth) return;

			const needsLoop = this.autoplay || availableWidth < textWidth;

			if (!needsLoop) {
				this.cleanupClones();
				return;
			}

			this.text.classList.add('ticker--animated');

			const targetCoverage = this.autoplay ? availableWidth * 2 : availableWidth + textWidth;
			const clonesNeeded = Math.max(1, Math.ceil(targetCoverage / textWidth));
			this.syncCloneCount(clonesNeeded);
			this.applyAnimationTiming(textWidth);
			this.attachPauseHandlers();
		}

		syncCloneCount(requiredClones) {
			const clones = Array.from(this.scale.querySelectorAll('[data-clone]'));
			const current = clones.length;

			if (current > requiredClones) {
				clones.slice(requiredClones).forEach((clone) => clone.remove());
				return;
			}

			for (let i = current; i < requiredClones; i += 1) {
				const clone = this.text.cloneNode(true);
				clone.setAttribute('data-clone', '');
				this.scale.appendChild(clone);
			}
		}

		cleanupClones() {
			this.text.classList.remove('ticker--animated');
			this.scale.querySelectorAll('[data-clone]').forEach((clone) => clone.remove());
		}

		applyAnimationTiming(textWidth) {
			const speedSetting = parseInt(this.getAttribute('data-marquee-speed'), 10);
			const speed = Number.isFinite(speedSetting) && speedSetting > 0 ? speedSetting : 100;
			const duration = (textWidth * 1.63) / speed;
			this.scale.style.setProperty('--animation-time', `${duration.toFixed(2)}s`);
		}

		attachPauseHandlers() {
			if (this.pauseListenersAttached) return;
			this.pauseListenersAttached = true;

			let hoverTimer;

			const setAnimationState = (state) => {
				this.querySelectorAll('[data-ticker-text]').forEach((node) => {
					node.style.animationPlayState = state;
				});
			};

			this.addEventListener('mouseenter', () => {
				clearTimeout(hoverTimer);
				hoverTimer = setTimeout(() => setAnimationState('paused'), 200);
			});

			this.addEventListener('mouseleave', () => {
				clearTimeout(hoverTimer);
				setAnimationState('running');
			});
		}
	}

	customElements.define('ticker-bar', TickerBar);
})();
